/**
 * useMealManagement - Hook for managing meal CRUD operations
 *
 * This hook provides meal management functionality for both PRO editor
 * and consumer dashboard, handling recipes, combos, foods, and leftovers.
 *
 * Features:
 * - Add/replace recipes with participant management
 * - Handle combo meals (main + side)
 * - Manage individual foods
 * - Update and remove meals
 * - Leftover creation and tracking
 * - Scaling calculations
 * - Works with both virtual (unsaved) and saved meals
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import { usePermissions } from '../contexts/PermissionsContext';
import Analytics from '@utils/Analytics';
import { getParticipantsForProfileByMealType, getMealTypeInfoByName, recalculateScaling, getParticipantsForSpecificMeal } from '@utils/Meals';
import { addDefaultAddSwapTags, convertEnvelopeToFilters, getNutrientsForMeals, getIdealsFromEnvelope } from '@utils/Nutrition';
import { getMealAndLeftovers } from '@utils/proMeals';

/**
 * Generate a short 8-character UUID (consistent with backend format)
 * @returns {string} 8-character hexadecimal UUID
 */
const generateShortUuid = () => {
    return uuid().substring(0, 8);
};

/**
 * Recalculate and apply scaling to a parent meal and its remaining leftovers
 *
 * @param {string} parentId - ID of the parent meal
 * @param {Array} meals - All meals array
 * @param {Object} recipes - Recipes lookup object
 * @param {Object} foods - Foods lookup object
 * @param {Object} profile - User/patient profile
 * @returns {Array} Updated meals array with new scaling applied, or original meals if no update needed
 */
export const recalculateAndApplyScalingForParent = (parentId, meals, recipes, foods, profile) => {
    const parentMeal = meals.find(m => (m.id === parentId || m.uuid === parentId) && !m.deleted);

    if (!parentMeal || (!parentMeal.recipe_uuid && !parentMeal.food_uuid)) {
        return meals;
    }

    const recipe = recipes[parentMeal.recipe_uuid];
    const food = foods[parentMeal.food_uuid];
    const content = recipe || food;

    if (!content) {
        return meals;
    }

    // Get remaining leftovers (not deleted)
    const mealFamily = getMealAndLeftovers(parentMeal, { items: meals });
    const remainingLeftovers = mealFamily.slice(1).filter(lo => !lo.deleted);

    const leftoversList = remainingLeftovers.map(leftover => ({
        uuid: leftover.uuid || leftover.id,
        leftoverOffset: leftover.offset,
        mealType: leftover.meal,
        participants: getParticipantsForSpecificMeal(leftover, profile) || []
    }));

    const parentParticipants = getParticipantsForSpecificMeal(parentMeal, profile);

    const scaling = recalculateScaling(
        parentMeal,
        parentParticipants,
        content,
        leftoversList,
        meals,
        profile
    );

    // Apply scaling to parent and remaining leftovers
    return meals.map(meal => {
        if (meal.id === parentMeal.id || meal.uuid === parentMeal.uuid) {
            return { ...meal, scaling };
        }
        const isRemainingLeftover = remainingLeftovers.find(lo =>
            (lo.id && lo.id === meal.id) || (lo.uuid && lo.uuid === meal.uuid)
        );
        if (isRemainingLeftover) {
            return { ...meal, scaling };
        }
        return meal;
    });
};

/**
 * Pure function to perform global cleanup - removes all deleted meals
 * @param {Array} meals - Current meals array
 * @returns {Array} New meals array with deleted meals removed
 */
export const performGlobalCleanup = (meals) => {
    const deletedMeals = meals.filter(meal => meal.deleted);
    const cleanedMeals = meals.filter(meal => !meal.deleted);

    return {
        cleanedMeals,
        deletedMealCount: deletedMeals.length
    };
};

/**
 * Pure function to restore all deleted meals
 * @param {Array} meals - Current meals array
 * @returns {Array} New meals array with deleted flags removed
 */
export const performRestoreAllDeleted = (meals) => {
    const deletedMeals = meals.filter(meal => meal.deleted);
    const restoredMeals = meals.map(meal => {
        if (meal.deleted) {
            const { deleted, ...restoredMeal } = meal;
            return restoredMeal;
        }
        return meal;
    });

    return {
        restoredMeals,
        restoredMealCount: deletedMeals.length
    };
};

/**
 * Build nutrition constraints for meal plan editor
 *
 * Determines the appropriate constraint scenario and builds nutrition filters:
 * 1. Adding to incomplete day (not last meal) → Use meal envelope constraints
 * 2. Adding the last meal of the day → Use daily reconciliation logic
 * 3. Swapping in incomplete day → Remove existing, apply scenario 1
 * 4. Swapping in complete day → Remove existing, apply scenario 2
 * 5. Adding to occupied slot → Combine with existing meal nutrition
 *
 * @param {Object} params - Parameters object
 * @param {Object} params.profile - User profile with preferences and daily totals
 * @param {Object} params.mealType - Meal type object with name and envelope
 * @param {Array} params.meals - All meals for the day
 * @param {Array} params.replaceMeals - Meals being replaced (for swapping)
 * @param {Object} params.contents - Recipe/food assets
 * @param {Object} params.modalSettings - Modal settings to modify
 * @returns {Object} Modified modal settings with nutrition constraints
 */
function buildMealPlanNutritionConstraints({profile, mealType, meals, replaceMeals = [], contents, modalSettings}) {
    // Get user's meal types and daily totals
    const userMealTypes = profile.preferences.meal_types?.map(mt => mt.name);
    const allDayRx = profile.preferences.daily_totals;
    const mealTypeName = mealType.name;

    // Skip if no daily totals defined
    if (!allDayRx) {
        return modalSettings;
    }

    // Skip if no meal envelope defined
    const mealEnvelope = mealType?.envelope;
    if (!mealEnvelope) {
        return modalSettings;
    }

    // Determine if we're swapping or adding
    const isSwapping = replaceMeals.length > 0;

    // Determine current day completion status
    const filledMealTypes = [...new Set(meals.map(m => m.meal))];

    // For swapping scenarios, check if the day would be complete after adding back the target meal type
    const isCompleteDay = isSwapping
        ? userMealTypes.every(type => filledMealTypes.includes(type) || type === mealTypeName)
        : userMealTypes.every(type => filledMealTypes.includes(type));


    // Calculate existing nutrition for this meal slot (all meals matching current mealType)
    const currentMealSlotMeals = meals.filter(meal => meal.meal === mealTypeName && !meal.deleted);
    const currentMealNutrients = getNutrientsForMeals(currentMealSlotMeals, contents, profile.portion);

    // Calculate nutrition from other meals on this day (excluding current meal type)
    const otherMealsThisDay = meals.filter(meal => meal.meal !== mealTypeName && !meal.deleted);
    const otherDayNutrients = getNutrientsForMeals(otherMealsThisDay, contents, profile.portion);


    // Determine scenario and build constraints
    let envelope;
    let shouldEnsureDailyTotalsAreMet = false;

    if (isSwapping && isCompleteDay) {
        // Scenario 4: Swapping in complete day → daily reconciliation
        shouldEnsureDailyTotalsAreMet = true;
    } else if (!isSwapping && !isCompleteDay && filledMealTypes.length === userMealTypes.length - 1) {
        // Check if we're adding to the actual empty slot (scenario 2) or to an occupied slot (scenario 5)
        const emptyMealType = userMealTypes.find(type => !filledMealTypes.includes(type));

        if (mealTypeName === emptyMealType) {
            // Scenario 2: Adding to the last empty slot → daily reconciliation
            shouldEnsureDailyTotalsAreMet = true;
        } else {
            // Scenario 5: Adding to occupied slot when day is almost complete → meal envelope only
            shouldEnsureDailyTotalsAreMet = false;
        }
    } else {
        // Scenarios 1, 3: Use meal envelope only
        shouldEnsureDailyTotalsAreMet = false;
    }

    if (shouldEnsureDailyTotalsAreMet) {
        // Complex daily reconciliation logic
        envelope = JSON.parse(JSON.stringify(mealEnvelope)); // deep copy

        // For each nutrient, ensure we meet daily requirements without exceeding limits
        Object.keys(envelope).forEach(nutrNo => {
            const allDayRange = allDayRx[nutrNo];
            const mealRange = envelope[nutrNo];

            if (!allDayRange) return;

            // Current consumption from other meals on this day
            const otherMealsConsumed = otherDayNutrients[nutrNo] || 0;

            // Handle both direct min/max and implicit.min/implicit.max
            let allDayMin = allDayRange.min;
            let allDayMax = allDayRange.max;
            let mealMin = mealRange.min;
            let mealMax = mealRange.max;

            // Check for implicit values
            if (typeof allDayMin !== 'number' && allDayRange.implicit?.min) {
                allDayMin = allDayRange.implicit.min;
            }
            if (typeof allDayMax !== 'number' && allDayRange.implicit?.max) {
                allDayMax = allDayRange.implicit.max;
            }
            if (typeof mealMin !== 'number' && mealRange.implicit?.min) {
                mealMin = mealRange.implicit.min;
            }
            if (typeof mealMax !== 'number' && mealRange.implicit?.max) {
                mealMax = mealRange.implicit.max;
            }

            // Calculate remaining needs and limits
            if (typeof allDayMin === 'number' && typeof mealMin === 'number') {
                const remainingNeeded = allDayMin - otherMealsConsumed;
                if (mealMin < remainingNeeded) {
                    envelope[nutrNo].min = remainingNeeded;
                }
            }

            if (typeof allDayMax === 'number' && typeof mealMax === 'number') {
                const remainingAllowed = allDayMax - otherMealsConsumed;
                if (mealMax > remainingAllowed) {
                    envelope[nutrNo].max = Math.max(0, remainingAllowed);
                }
            }
        });
    } else {
        // Simple meal envelope constraints
        envelope = mealEnvelope;
    }

    // Add daily maximum clamping regardless of scenario to prevent exceeding daily totals
    if (allDayRx && envelope) {
        envelope = JSON.parse(JSON.stringify(envelope)); // ensure we have a modifiable copy

        Object.keys(envelope).forEach(nutrNo => {
            const allDayRange = allDayRx[nutrNo];
            const mealRange = envelope[nutrNo];

            if (!allDayRange || !mealRange) return;

            // Get daily maximum (check both direct max and implicit.max)
            let allDayMax = allDayRange.max;
            if (typeof allDayMax !== 'number' && allDayRange.implicit?.max) {
                allDayMax = allDayRange.implicit.max;
            }

            // Get current meal range maximum
            let mealMax = mealRange.max;
            if (typeof mealMax !== 'number' && mealRange.implicit?.max) {
                mealMax = mealRange.implicit.max;
            }

            // Apply daily maximum clamping if both values are numeric
            if (typeof allDayMax === 'number' && typeof mealMax === 'number') {
                const consumedFromOtherMeals = otherDayNutrients[nutrNo] || 0;
                const remainingAllowed = allDayMax - consumedFromOtherMeals;

                if (remainingAllowed >= 0 && mealMax > remainingAllowed) {
                    envelope[nutrNo].max = Math.max(0, remainingAllowed);
                }
            }
        });
    }

    // Convert envelope to search filters
    modalSettings.extraFilters = convertEnvelopeToFilters(envelope, modalSettings.extraFilters, 1, profile.portion);

    // Set ideals for Smart Choices
    modalSettings.ideals = getIdealsFromEnvelope(envelope, profile.portion);

    return modalSettings;
}

/**
 * Get default add/swap settings with nutritional intelligence and filtering
 *
 * This function prepares modal settings for the AddSwapRecipe modal by:
 * - Calculating participants for the meal type
 * - Setting up base filters and exclusions
 * - Adding leftover-aware serving calculations
 * - Applying user preferences (taste, skill, equipment)
 * - Adding nutritional constraints from prescriptions
 *
 * @param {Array<{uuid: string, portion: number}>} participants - Array of participant objects with uuid and portion size
 * @param {number} offset - Day offset from plan start date (0-6 for week view)
 * @param {Object} mealType - Meal type object containing name and other meal type properties
 * @param {Array<Object>} replaceMeals - Array of meal objects being replaced (used for exclusion)
 * @param {Object} options - Additional options to merge into modal settings
 * @param {Object} profile - User profile containing preferences, conditions, and nutritional settings
 * @param {Object} plan - Current meal plan object with start_date, uuid, title, etc.
 * @param {Object.<string, Object>} recipes - Recipe lookup object keyed by recipe UUID
 * @param {Array<Object>} meals - All meals for current day (used for nutritional context)
 * @param {boolean} leftoversEnabled - Whether leftover functionality is enabled in user preferences
 * @param {number} maxLeftoverDays - Maximum number of days leftovers can be kept (typically 1-3)
 * @returns {Object} Modal settings object containing filters, tags, exclusions, and nutritional constraints
 */
function getDefaultAddSwapSettings(
    participants,
    offset,
    mealType,
    replaceMeals = [],
    options = {},
    profile,
    plan,
    recipes,
    foods,
    meals,
    leftoversEnabled,
    maxLeftoverDays
) {
    const modalSettings = {
        ...options,
        defaultTypes: ['recipe', 'combo', 'food'],
        allowedTypes: ['recipe', 'combo', 'food', 'collection'],
        defaultTags: [],
        defaultAvoids: [],
        defaultExcludes: [
            'Condiment/Sauce',
            'Baby Food',
            'Purée',
            'Foundational',
            'Thanksgiving',
            'Christmas',
            'Supplement',
        ],
        extraFilters: {},
        ideals: {},
        editableParticipants: true, // TODO: Get this from props if needed
        // Fix for Flow 1.2.1: Enable auto-search in FullBrowser when modal opens
        fullBrowserParams: {
            inhibitSearchOnMount: false,
            ...options.fullBrowserParams
        },
        // Fix for Flow 1.2.1: Ensure nutrition filters are applied to search results
        useComputedParams: true
    };

    if (leftoversEnabled) {
        let servingsNeeded = participants.reduce((total, member) => total + member.portion, 0);

        modalSettings.extraFilters.sizes = Math.ceil(servingsNeeded);
        modalSettings.extraFilters.servings = { lte: Math.ceil(servingsNeeded * (maxLeftoverDays + 1)) };
    }

    // Get a list of recipe & foods we're about to replace so we can exclude them
    const excludeUuids = replaceMeals.map((meal) => meal.recipe_uuid);

    const addSwapParams = {
        plan,
        mealType,
        replaceMeals,
        meals,
        contents: {...recipes, ...foods},
        profile,
        modalSettings,
        excludeUuids,
    };

    addDefaultAddSwapTags(addSwapParams);

    // Use new meal plan nutrition constraints instead of legacy function
    buildMealPlanNutritionConstraints({
        profile: addSwapParams.profile,
        mealType: addSwapParams.mealType,
        meals: addSwapParams.meals,
        replaceMeals: addSwapParams.replaceMeals,
        contents: addSwapParams.contents,
        modalSettings: addSwapParams.modalSettings,
    });

    return modalSettings;
}

/**
 * Configuration for meal management
 * @typedef {Object} MealManagementConfig
 * @property {Array} meals - Current meals array
 * @property {Function} onMealsChange - Callback when meals are modified.
 *   Called with the new meals array: onMealsChange(newMealsArray)
 * @property {Object} profile - User profile with preferences and portion
 * @property {Object} assets - Recipe/food assets containing recipes and foods objects
 * @property {Function} generateUuid - UUID generator function
 * @property {boolean} trackHistory - Whether to track operation history
 * @property {Object} plan - Current meal plan for Analytics context
 */

/**
 * Hook for meal management operations
 *
 * @param {MealManagementConfig} config - Configuration object
 * @returns {Object} Meal management functions and state
 */
export const useMealManagement = (config = {}) => {
    const {
        meals = [],
        onMealsChange = () => {},
        profile = {},
        assets = null,
        generateUuid = uuid,
        trackHistory = false,
        modalManager = null,
        plan = {}
    } = config;

    // Extract recipes and foods from assets
    const recipes = assets?.recipes || {};
    const foods = assets?.foods || {};

    // Get permissions for validation
    const permissions = usePermissions();

    // Track operation history if enabled
    const [history, setHistory] = useState([]);
    const [error, setError] = useState(null);

    // Global debounced cleanup timer for all soft-deleted meals
    const globalCleanupTimer = useRef(null);
    const SOFT_DELETE_CLEANUP_DELAY = 5000; // 5 seconds

    // Ref to current meals for timer callbacks to avoid stale closures
    const currentMealsRef = useRef(meals);
    useEffect(() => {
        currentMealsRef.current = meals;
    }, [meals]);

    /**
     * Add operation to history
     */
    const addToHistory = useCallback((operation) => {
        if (!trackHistory) return;

        setHistory(prev => [...prev, {
            ...operation,
            timestamp: Date.now()
        }]);
    }, [trackHistory]);

    /**
     * Start global debounced cleanup timer - resets on each deletion
     * When fired, removes ALL soft-deleted meals from the plan
     */
    const startGlobalCleanup = useCallback(() => {
        // Cancel any existing global timer
        if (globalCleanupTimer.current) {
            clearTimeout(globalCleanupTimer.current);
        }

        // Schedule new global cleanup
        globalCleanupTimer.current = setTimeout(() => {
            // Use ref to get current meals and avoid stale closures
            const { cleanedMeals, deletedMealCount } = performGlobalCleanup(currentMealsRef.current);

            // Clear timer reference
            globalCleanupTimer.current = null;

            addToHistory({
                type: 'global_auto_cleanup',
                deletedMealCount,
                cleanedAt: Date.now()
            });

            onMealsChange(cleanedMeals);
        }, SOFT_DELETE_CLEANUP_DELAY);
    }, [onMealsChange, addToHistory]);

    /**
     * Cancel global cleanup timer
     */
    const cancelGlobalCleanup = useCallback(() => {
        if (globalCleanupTimer.current) {
            clearTimeout(globalCleanupTimer.current);
            globalCleanupTimer.current = null;
        }
    }, []);

    /**
     * Restore ALL soft-deleted meals in the plan
     */
    const restoreAllDeletedMeals = useCallback(() => {
        // Use ref to get current meals and avoid stale closures
        const { restoredMeals, restoredMealCount } = performRestoreAllDeleted(currentMealsRef.current);

        addToHistory({
            type: 'restore_all_deleted',
            restoredMealCount,
            restoredAt: Date.now()
        });

        onMealsChange(restoredMeals);
    }, [onMealsChange, addToHistory]);

    /**
     * Clear global cleanup timer on unmount
     */
    useEffect(() => {
        return () => {
            if (globalCleanupTimer.current) {
                clearTimeout(globalCleanupTimer.current);
                globalCleanupTimer.current = null;
            }
        };
    }, []);

    /**
     * Get reschedule overlaps - detects conflicts when moving meals
     */
    const getRescheduleOverlaps = useCallback((mealsToReschedule, targetOffset, targetMealType) => {
        const overlaps = [];
        const clears = [];

        // Get IDs of meals being rescheduled
        const idsToReschedule = mealsToReschedule.map(m => m.uuid || m.id);

        // Find meals at the destination that would be overwritten
        const destMeals = meals.filter(item => {
            return (
                !item.deleted &&
                item.meal === targetMealType &&
                item.offset === targetOffset &&
                !idsToReschedule.includes(item.uuid) &&
                !idsToReschedule.includes(item.id) &&
                !idsToReschedule.includes(item.parent) &&
                !idsToReschedule.includes(item.parent)
            );
        });

        if (destMeals.length > 0) {
            overlaps.push({
                mealType: targetMealType,
                offset: targetOffset,
                destMeals,
                sourceMeals: mealsToReschedule
            });
        } else {
            clears.push(targetOffset);
        }

        return {
            mealsToReschedule,
            overlaps,
            clears
        };
    }, [meals]);

    /**
     * Get reschedule overlaps V3 - mirrors consumer side logic from Meals.js getRescheduleOverlaps
     * Determines when to show leftover modal vs confirm overwrite modal
     * 
     * @param {Array} mealsToReschedule - Meals being moved
     * @param {number} targetOffset - Target offset (0-6 for days)
     * @param {string} targetMealType - Target meal type (Breakfast, Lunch, etc.)
     * @param {Array} replaceMeals - Meals being replaced (optional)
     * @param {Array} userLeftovers - User-selected leftovers (optional)
     * @returns {Object} Reschedule data including showLeftOverOffestModal flag
     */
    const getRescheduleOverlapsV3 = useCallback((mealsToReschedule, targetOffset, targetMealType, replaceMeals = [], userLeftovers = null) => {
        let overlaps = [], clears = [];

        // Safety check for parameters
        if (!mealsToReschedule || !Array.isArray(mealsToReschedule) || mealsToReschedule.length === 0) {
            console.error('getRescheduleOverlapsV3: Invalid mealsToReschedule parameter:', mealsToReschedule);
            return { mealsToReschedule: [], overlaps, clears };
        }

        // Get primary meal and check if it's a recipe/food (mirrors consumer lines 1321-1322)
        const primaryMeal = mealsToReschedule[0];
        if (!primaryMeal) {
            return { mealsToReschedule, overlaps, clears };
        }

        const uuidsToReschedule = mealsToReschedule.map(m => m.uuid || m.id);
        const isLeftover = !mealsToReschedule.some(m => m.meal_type !== 'leftover');
        const replaceMealIds = (replaceMeals || []).map(m => m.uuid || m.id);

        // Find meals at the destination (mirrors consumer lines 1328-1334)
        const destMeals = meals.filter(m => !m.deleted &&
                                            m.meal === targetMealType &&
                                            m.offset === targetOffset &&
                                            !uuidsToReschedule.includes(m.uuid) &&
                                            !uuidsToReschedule.includes(m.id) &&
                                            !uuidsToReschedule.includes(m.parent) &&
                                            !uuidsToReschedule.includes(m.parent) &&
                                            !replaceMealIds.includes(m.uuid) &&
                                            !replaceMealIds.includes(m.id));

        if (destMeals.length) {
            overlaps.push({
                mealType: targetMealType,
                offset: targetOffset,
                destMeals,
                sourceMeals: mealsToReschedule,
            });
        } else {
            clears.push({
                mealType: targetMealType,
                offset: targetOffset,
                sourceMeals: mealsToReschedule,
            });
        }

        // Get participants (mirrors consumer line 1350)
        const participants = []; // TODO: Implement participant extraction if needed

        // Get meal and its leftovers using proper PRO utility (mirrors consumer line 1351)
        const mealAndLeftovers = getMealAndLeftovers(primaryMeal, { items: meals });

        // Get meal types for comparison (mirrors consumer lines 1353-1355)
        const availableMealTypes = plan.meal_types || profile.preferences.meal_types || [];
        const destinationMealTypeIndex = availableMealTypes.findIndex((mt) => mt.name === targetMealType);
        const leftOverMealIndex = mealAndLeftovers[1] ? 
            availableMealTypes.findIndex((mt) => mt.name === mealAndLeftovers[1].meal) : -1;

        // Check if moving BEFORE leftovers (mirrors consumer lines 1357-1360)
        const isBeforeLeftovers = mealAndLeftovers.length > 1 && (
            (targetOffset < mealAndLeftovers[1].offset) || 
            (targetOffset === mealAndLeftovers[1].offset && destinationMealTypeIndex < leftOverMealIndex)
        );

        // Apply consumer logic (mirrors consumer lines 1362-1374)
        if (isBeforeLeftovers || isLeftover || mealAndLeftovers.length === 1) {
            // Just show confirm overwrite modal - no leftover modal needed
            return { mealsToReschedule, overlaps, clears };
        } else if (mealAndLeftovers.length > 1 && !userLeftovers) {
            // Show leftover modal - moving meal with leftovers to spot AFTER first leftover

            // For plans/editor, leftoverOffset should be after target
            const leftoverOffset = targetOffset + 1;

            return {
                primary: primaryMeal,
                mealsToReschedule,
                leftoverOffset,
                participants,
                showLeftOverOffestModal: true,
                overlaps,
                clears
            };
        } else if (userLeftovers) {
            // User has selected leftovers - process them (mirrors consumer lines 1375-1408)
            // This handles the case when leftover modal returns with user selections
            
            
            userLeftovers.forEach(({leftoverOffset: leftoverTargetOffset, mealType: leftoverMealType, participants: leftoverParticipants}, index) => {
                
                
                // Find the meals at the destination that would be overwritten
                const destMeals = meals.filter(m => m.meal === leftoverMealType &&
                                                    m.offset === leftoverTargetOffset &&
                                                    !uuidsToReschedule.includes(m.uuid) &&
                                                    !uuidsToReschedule.includes(m.id) &&
                                                    !uuidsToReschedule.includes(m.parent) &&
                                                    !uuidsToReschedule.includes(m.parent));

                let sourceMeals = [];
                
                // Process all meals to reschedule, not just primary meal
                mealsToReschedule.forEach(meal => {
                    const mealAndItsLeftovers = getMealAndLeftovers(meal, { items: meals });
                    const leftoverForThisMeal = mealAndItsLeftovers[index + 1];
                    
                    
                    if (leftoverForThisMeal) {
                        sourceMeals.push(leftoverForThisMeal);
                    }
                });

                // If there are already meals at our destination
                if (destMeals.length && sourceMeals.length) {
                    overlaps.push({
                        mealType: leftoverMealType,
                        offset: leftoverTargetOffset,
                        destMeals,
                        sourceMeals,
                    });
                } else if (sourceMeals.length) {
                    clears.push({
                        mealType: leftoverMealType,
                        offset: leftoverTargetOffset,
                        sourceMeals,
                    });
                }
            });

            return { mealsToReschedule, overlaps, clears };
        }

        // Default fallback
        return { mealsToReschedule, overlaps, clears };
    }, [meals, plan, profile]);

    /**
     * Complete reschedule operation when there are no overlaps
     * Mirrors PRO Editor completeRescheduleMeals functionality
     * 
     * @param {Array} mealsToReschedule - Meals to move to new position
     * @param {number} targetOffset - Target offset 
     * @param {string} targetMealType - Target meal type
     * @param {Array} clears - Clear operations for leftover meals
     * @param {boolean} closeModal - Whether to close leftover modal
     */
    const completeRescheduleMeals = useCallback((mealsToReschedule, targetOffset, targetMealType, clears, closeModal = false) => {

        // Update main meals (mirrors PRO Editor logic)
        const updatedMainMeals = mealsToReschedule.map(meal => ({
            ...meal,
            offset: targetOffset,
            meal: targetMealType
        }));

        // Create update map for all meals that need to be moved
        const updateMap = new Map();
        
        // Add main meal updates
        updatedMainMeals.forEach(meal => {
            const mealId = meal.uuid || meal.id;
            updateMap.set(mealId, {
                offset: targetOffset,
                meal: targetMealType
            });
        });

        // Process clears to update leftover meals (mirrors consumer completeRescheduleMeals)
        clears.forEach(clear => {
            if (clear.sourceMeals) {
                clear.sourceMeals.forEach(sourceMeal => {
                    const mealId = sourceMeal.uuid || sourceMeal.id;
                    updateMap.set(mealId, {
                        offset: clear.offset,
                        meal: clear.mealType
                    });
                });
            }
        });

        // Apply all updates in a single state change
        const newMeals = meals.map(meal => {
            const mealId = meal.uuid || meal.id;
            const changes = updateMap.get(mealId);

            if (changes) {
                return {
                    ...meal,
                    ...changes
                };
            }
            return meal;
        });
        onMealsChange(newMeals);

        // Close leftover modal if requested (mirrors PRO Editor this.closeModal())
        if (closeModal && modalManager?.closeLeftoverModal) {
            modalManager.closeLeftoverModal();
        }

        return { success: true };
    }, [meals, onMealsChange, modalManager]);

    /**
     * Reschedule meals with overlap detection
     * Returns overlap data if conflicts exist, otherwise performs the reschedule
     */
    const rescheduleMeals = useCallback((mealsToMove, targetOffset, targetMealType, userLeftovers) => {
        
        setError(null);

        // Check permissions
        if (permissions.inhibitSwap) {
            const error = new Error('Permission denied: meal modifications are disabled (inhibitSwap)');
            setError(error);
            return { success: false, error };
        }

        // Use getRescheduleOverlapsV3 which mirrors consumer-side logic
        const result = getRescheduleOverlapsV3(
            mealsToMove,
            targetOffset,
            targetMealType,
            [], // replaceMeals - empty for regular reschedule
            userLeftovers
        );

        // Check if we should show leftover modal (mirrors consumer pattern)
        if (result.showLeftOverOffestModal) {

            const leftoverParams = {
                leftoverOffset: result.leftoverOffset,
                meal: result.primary,
                participants: result.participants,
                mealsToReschedule: result.mealsToReschedule,
                targetOffset,
                targetMealType
            };

            modalManager.openLeftoverOffsetsModal(leftoverParams);
            return { success: true, openedLeftoverModal: true };
        }

        // Extract the standard reschedule data
        const { mealsToReschedule, overlaps, clears } = result;

        // If no overlaps, complete the reschedule (mirrors PRO Editor logic)
        if (overlaps.length === 0) {
            const shouldCloseModal = !!userLeftovers; // Close modal if this was triggered by userLeftovers
            return completeRescheduleMeals(mealsToReschedule, targetOffset, targetMealType, clears, shouldCloseModal);
        }

        // Show overlap resolution modal if modalManager is available
        if (modalManager?.openOverlapModal) {
            modalManager.openOverlapModal({
                overlaps,
                mealsToReschedule,
                clears,
                targetOffset,
                targetMealType
            });
            return { success: true, hasOverlaps: true };
        }

        // Fallback: return overlap data for external handling
        return {
            success: true,
            hasOverlaps: true,
            overlapData: {
                overlaps,
                mealsToReschedule,
                clears,
                targetOffset,
                targetMealType
            }
        };
    }, [getRescheduleOverlapsV3, meals, onMealsChange, setError, permissions, modalManager]);

    /**
     * Apply overlap resolution changes to meals (used by modal manager)
     */
    const applyOverlapResolution = useCallback((dirtyMeals, mealsToDelete, leftoverOffsets) => {
        
        setError(null);

        // Apply the changes
        let newMeals = [...meals];

        // Delete meals with proper leftover cleanup
        if (mealsToDelete && mealsToDelete.length > 0) {
            const deleteIds = mealsToDelete.map(m => m.uuid || m.id);

            // Find all leftovers for each meal being deleted and include them in deletion
            const allMealIds = new Set(deleteIds);
            deleteIds.forEach(mealId => {
                const leftovers = getLeftoversForMeal(mealId);
                leftovers.forEach(leftover => {
                    allMealIds.add(leftover.uuid || leftover.id);
                });
            });

            // Hard delete all meals and their leftovers (no soft delete for conflict resolution)
            newMeals = newMeals.filter(meal =>
                !allMealIds.has(meal.uuid) && !allMealIds.has(meal.id)
            );
        }

        // Update meals
        if (dirtyMeals && dirtyMeals.length > 0) {
            const updateMap = new Map(dirtyMeals.map(meal => [meal.uuid || meal.id, meal]));

            newMeals = newMeals.map(meal => {
                const mealId = meal.uuid || meal.id;
                if (updateMap.has(mealId)) {
                    return { ...meal, ...updateMap.get(mealId) };
                }
                return meal;
            });

            // Add any new meals that weren't updates
            dirtyMeals.forEach(meal => {
                const mealId = meal.uuid || meal.id;
                if (!newMeals.some(m => (m.uuid === mealId) || (m.id === mealId))) {
                    newMeals.push(meal);
                }
            });
        }

        onMealsChange(newMeals);

        addToHistory({
            type: 'overlap_resolved',
            updates: dirtyMeals,
            deletes: mealsToDelete,
            leftoverOffsets
        });

        return { success: true };
    }, [meals, onMealsChange, addToHistory, getLeftoversForMeal]);

    /**
     * Get meals by type
     */
    const getMealsByType = useCallback((mealType) => {
        return meals.filter(meal => meal.meal === mealType && !meal.deleted);
    }, [meals]);

    /**
     * Get meals by offset and meal type
     */
    const getMealsByOffsetAndType = useCallback((offset, mealType) => {
        return meals.filter(meal =>
            meal.offset === offset &&
            meal.meal === mealType &&
            !meal.deleted
        );
    }, [meals]);

    /**
     * Get leftovers for a meal
     */
    const getLeftoversForMeal = useCallback((mealId) => {
        return meals.filter(meal => meal.parent === mealId && !meal.deleted);
    }, [meals]);

    /**
     * Check if meal slot has main/side dishes
     */
    const getMealSlotInfo = useCallback((mealType) => {
        const slotMeals = getMealsByType(mealType);

        return {
            hasMain: slotMeals.some(m => !m.side_dish),
            hasSide: slotMeals.some(m => m.side_dish),
            meals: slotMeals,
            count: slotMeals.length
        };
    }, [getMealsByType]);

    // ============================================================================
    // NEW EXPLICIT CRUD OPERATIONS
    // ============================================================================

    /**
     * Add a single meal (explicit CRUD operation)
     *
     * @param {Object} meal - Complete meal object to add
     * @returns {Object} Result with success status and meal ID
     */
    const addMeal = useCallback((meal) => {
        setError(null);

        // Check permissions
        if (permissions.inhibitSwap) {
            const error = new Error('Permission denied: meal modifications are disabled (inhibitSwap)');
            setError(error);
            return { success: false, error };
        }

        // Ensure meal has required properties for Editor context
        const mealToAdd = {
            id: meal.id || generateUuid(),
            ...meal
        };

        // Ensure participants is always an array of participant IDs (at least one)
        // The primary profile participant should always be 'USER'
        if (!mealToAdd.participants || !Array.isArray(mealToAdd.participants) || mealToAdd.participants?.length === 0) {
            mealToAdd.participants = ['USER'];
        }

        const newMeals = [...meals, mealToAdd];
        onMealsChange(newMeals);

        addToHistory({
            type: 'meal_added',
            mealId: mealToAdd.id,
            meal: mealToAdd
        });

        return { success: true, mealId: mealToAdd.id };
    }, [meals, onMealsChange, generateUuid, addToHistory, permissions, profile]);

    /**
     * Add multiple meals (explicit CRUD operation)
     *
     * @param {Array} mealsToAdd - Array of meal objects to add
     * @returns {Object} Result with success status and array of meal IDs
     */
    const addMeals = useCallback((mealsToAdd) => {
        setError(null);

        // Check permissions
        if (permissions.inhibitSwap) {
            const error = new Error('Permission denied: meal modifications are disabled (inhibitSwap)');
            setError(error);
            return { success: false, error };
        }

        if (mealsToAdd.length === 0) {
            return { success: true, mealIds: [] };
        }

        const processedMeals = mealsToAdd.map(meal => {
            // Ensure meal has required properties for Editor context
            const mealToAdd = {
                id: meal.id || generateUuid(),
                ...meal
            };

            // Ensure participants is always an array of participant IDs (at least one)
            // The primary profile participant should always be 'USER'
            if (!mealToAdd.participants || !Array.isArray(mealToAdd.participants) || mealToAdd.participants?.length === 0) {
                mealToAdd.participants = ['USER'];
            }

            return mealToAdd;
        });

        const newMeals = [...meals, ...processedMeals];
        onMealsChange(newMeals);

        addToHistory({
            type: 'meals_added',
            mealIds: processedMeals.map(m => m.id),
            meals: processedMeals,
            count: processedMeals.length
        });

        return { success: true, mealIds: processedMeals.map(m => m.id) };
    }, [meals, onMealsChange, generateUuid, addToHistory, permissions, profile]);

    /**
     * Update a single meal (explicit CRUD operation)
     *
     * @param {string} mealId - ID of the meal to update
     * @param {Object} updates - Properties to update
     * @returns {Object} Result with success status
     */
    const updateMeal = useCallback((mealId, updates) => {
        setError(null);

        // Check permissions
        if (permissions.inhibitSwap) {
            const error = new Error('Permission denied: meal modifications are disabled (inhibitSwap)');
            setError(error);
            return { success: false, error };
        }

        // Find the meal to update
        const mealIndex = meals.findIndex(meal => meal.uuid === mealId || meal.id === mealId);

        // Create updated meal
        const updatedMeal = {
            ...meals[mealIndex],
            ...updates
        };

        // In Editor context, participants should be array of IDs
        if (updates.participants && Array.isArray(updates.participants)) {
            updatedMeal.participants = updates.participants;
        }

        const newMeals = [...meals];
        newMeals[mealIndex] = updatedMeal;
        onMealsChange(newMeals);

        addToHistory({
            type: 'meal_updated',
            mealId,
            updates,
            previousValues: Object.keys(updates).reduce((prev, key) => {
                prev[key] = meals[mealIndex][key];
                return prev;
            }, {})
        });

        return { success: true, mealId };
    }, [meals, onMealsChange, generateUuid, addToHistory, permissions]);


    /**
     * Remove a single meal (explicit CRUD operation)
     *
     * @param {string} mealId - ID of the meal to remove
     * @param {boolean} hardDelete - Whether to permanently delete or soft delete
     * @returns {Object} Result with success status
     */
    const removeMeal = useCallback((mealId, hardDelete = false) => {
        setError(null);

        // Check permissions
        if (permissions.inhibitSwap) {
            const error = new Error('Permission denied: meal modifications are disabled (inhibitSwap)');
            setError(error);
            return { success: false, error };
        }

        // Find the meal being deleted to check if it's a leftover
        const mealBeingDeleted = meals.find(m => m.uuid === mealId || m.id === mealId);
        const isLeftover = mealBeingDeleted?.meal_type === 'leftover';

        // If deleting a leftover, only delete that leftover (not parent and siblings)
        // If deleting a main meal, delete it and all its leftovers
        let allMealIds;
        let leftovers = [];
        if (isLeftover) {
            allMealIds = [mealId];
        } else {
            leftovers = getLeftoversForMeal(mealId);
            allMealIds = [mealId, ...leftovers.map(leftover => leftover.uuid || leftover.id)];
        }

        let newMeals;

        if (hardDelete) {
            // Hard delete: Remove all related meals (parent + leftovers)
            newMeals = meals.filter(meal =>
                !allMealIds.includes(meal.uuid) && !allMealIds.includes(meal.id)
            );

        } else {
            // Soft delete: Mark all related meals (parent + leftovers) as deleted
            newMeals = meals.map(meal => {
                if (allMealIds.includes(meal.uuid) || allMealIds.includes(meal.id)) {
                    return { ...meal, deleted: true };
                }
                return meal;
            });

            // Start global debounced cleanup timer (resets if already running)
            startGlobalCleanup();
        }

        // If deleting a leftover, recalculate scaling for parent and remaining leftovers
        if (isLeftover && mealBeingDeleted?.parent) {
            newMeals = recalculateAndApplyScalingForParent(
                mealBeingDeleted.parent,
                newMeals,
                recipes,
                foods,
                profile
            );
        }

        onMealsChange(newMeals);

        // Analytics tracking for recipe removal
        const removedMeal = meals.find(meal => meal.uuid === mealId || meal.id === mealId);
        if (removedMeal && removedMeal.recipe_uuid && recipes[removedMeal.recipe_uuid]) {
            Analytics.removeRecipe({
                'Meal Plan UUID': plan.uuid,
                'Meal Plan Name': plan.title,
                'Offset': removedMeal.offset,
                'Meal Type': removedMeal.meal
            });
        }

        addToHistory({
            type: 'meal_removed',
            mealId,
            hardDelete,
            // Bug #5 Fix: Track that leftovers were also deleted
            relatedMealIds: allMealIds,
            leftoverCount: leftovers.length
        });

        return { success: true, mealId, relatedMealIds: allMealIds };
    }, [meals, onMealsChange, addToHistory, permissions, recipes, foods, profile, plan, getLeftoversForMeal, startGlobalCleanup]);

    /**
     * Remove multiple meals (explicit CRUD operation)
     *
     * @param {Array} mealIds - Array of meal IDs to remove
     * @param {boolean} hardDelete - Whether to permanently delete or soft delete
     * @returns {Object} Result with success status
     */
    const removeMeals = useCallback((mealIds, hardDelete = false) => {
        setError(null);

        if (mealIds.length === 0) {
            return { success: true, mealIds: [] };
        }

        // Identify which meals are leftovers and track their parents for scaling recalculation
        const leftoversBeingDeleted = [];
        const parentsNeedingRecalc = new Set();

        mealIds.forEach(mealId => {
            const meal = meals.find(m => m.uuid === mealId || m.id === mealId);
            if (meal && meal.meal_type === 'leftover' && meal.parent) {
                leftoversBeingDeleted.push(meal);
                parentsNeedingRecalc.add(meal.parent);
            }
        });

        // Bug #5 Fix: Find all leftovers for each meal being deleted
        const allMealIds = new Set(mealIds);
        mealIds.forEach(mealId => {
            const leftovers = getLeftoversForMeal(mealId);
            leftovers.forEach(leftover => {
                allMealIds.add(leftover.uuid || leftover.id);
            });
        });

        const allMealIdsArray = Array.from(allMealIds);
        let newMeals;

        if (hardDelete) {
            // Hard delete: Remove all related meals (parent + leftovers)
            newMeals = meals.filter(meal =>
                !allMealIds.has(meal.uuid) && !allMealIds.has(meal.id)
            );

        } else {
            // Soft delete: Mark all related meals (parent + leftovers) as deleted
            newMeals = meals.map(meal => {
                if (allMealIds.has(meal.uuid) || allMealIds.has(meal.id)) {
                    return { ...meal, deleted: true };
                }
                return meal;
            });

            // Schedule global cleanup for all deleted meals after delay
            startGlobalCleanup();
        }

        // Recalculate scaling for parents of deleted leftovers
        parentsNeedingRecalc.forEach(parentId => {
            newMeals = recalculateAndApplyScalingForParent(parentId, newMeals, recipes, foods, profile);
        });

        onMealsChange(newMeals);

        // Analytics tracking for batch recipe removal
        const removedMealsWithRecipes = meals.filter(meal =>
            (allMealIds.has(meal.uuid) || allMealIds.has(meal.id)) &&
            meal.recipe_uuid &&
            recipes[meal.recipe_uuid]
        );

        // Track Analytics once for all removed recipes (if any)
        if (removedMealsWithRecipes.length > 0) {
            const recipeUUIDs = removedMealsWithRecipes.map(meal => meal.recipe_uuid);
            const recipeNames = removedMealsWithRecipes
                .map(meal => recipes[meal.recipe_uuid]?.title)
                .filter(Boolean)
                .join(' + ');

            // Use first meal's context for offset and meal type
            const firstMeal = removedMealsWithRecipes[0];

            Analytics.removeRecipe({
                UUIDs: recipeUUIDs,
                'Recipe Name': recipeNames,
                'Meal Plan UUID': plan.uuid,
                'Meal Plan Name': plan.title,
                'Offset': firstMeal.offset,
                'Meal Type': firstMeal.meal
            });
        }

        addToHistory({
            type: 'meals_removed',
            mealIds: allMealIdsArray,
            hardDelete,
            count: allMealIdsArray.length,
            // Bug #5 Fix: Track original vs total count
            originalMealIds: mealIds,
            originalCount: mealIds.length
        });

        return { success: true, mealIds: allMealIdsArray };
    }, [meals, onMealsChange, addToHistory, recipes, plan, getLeftoversForMeal, startGlobalCleanup]);


    /**
     * Execute multiple operations as a batch (explicit CRUD operation)
     *
     * This implementation creates temporary versions of CRUD operations that
     * accumulate changes in local state, then applies all changes at once.
     *
     * @param {Function} operations - Function that performs multiple operations
     * @returns {Object} Result with success status
     */
    const batchOperations = useCallback((operations) => {
        setError(null);

        // Store initial state and track changes locally
        const initialMeals = meals;
        let currentMeals = [...meals]; // Working copy for batch operations
        const batchStartTime = Date.now();

        // Create batch-aware versions of CRUD operations
        const batchContext = {
            removeMeal: (mealId, hardDelete = false) => {
                // Note: Analytics tracking is handled separately for batch operations
                // to aggregate multiple recipes into a single Analytics call

                if (hardDelete) {
                    currentMeals = currentMeals.filter(meal => meal.uuid !== mealId && meal.id !== mealId);

                } else {
                    currentMeals = currentMeals.map(meal => {
                        if (meal.uuid === mealId || meal.id === mealId) {
                            return { ...meal, deleted: true };
                        }
                        return meal;
                    });

                    // Schedule global cleanup for deleted meals after delay
                    startGlobalCleanup();
                }

                return { success: true, mealId };
            },

            addMeals: (mealsToAdd) => {
                if (mealsToAdd.length === 0) {
                    return { success: true, mealIds: [] };
                }

                const processedMeals = mealsToAdd.map(meal => {
                    const mealToAdd = {
                        id: meal.id || generateUuid(),
                        ...meal
                    };

                    // The primary profile participant should always be 'USER'
                    if (!mealToAdd.participants ||
                        !Array.isArray(mealToAdd.participants) ||
                        mealToAdd.participants?.length === 0) {
                        mealToAdd.participants = ['USER'];
                    }

                    return mealToAdd;
                });

                currentMeals = [...currentMeals, ...processedMeals];

                return { success: true, mealIds: processedMeals.map(m => m.id) };
            }
        };

        // Execute operations with batch context
        const result = operations(batchContext);

        // Track Analytics for any recipe removals that occurred during batch operations
        const removedMealsWithRecipes = initialMeals.filter(initialMeal => {
            const stillExists = currentMeals.some(currentMeal =>
                (currentMeal.uuid === initialMeal.uuid || currentMeal.id === initialMeal.id) &&
                !currentMeal.deleted
            );

            return !stillExists &&
                   initialMeal.recipe_uuid &&
                   recipes[initialMeal.recipe_uuid];
        });

        if (removedMealsWithRecipes.length > 0) {
            const recipeUUIDs = removedMealsWithRecipes.map(meal => meal.recipe_uuid);
            const recipeNames = removedMealsWithRecipes
                .map(meal => recipes[meal.recipe_uuid]?.title)
                .filter(Boolean)
                .join(' + ');

            // Use first meal's context for offset and meal type
            const firstMeal = removedMealsWithRecipes[0];

            Analytics.removeRecipe({
                UUIDs: recipeUUIDs,
                'Recipe Name': recipeNames,
                'Meal Plan UUID': plan.uuid,
                'Meal Plan Name': plan.title,
                'Offset': firstMeal.offset,
                'Meal Type': firstMeal.meal
            });
        }

        // Apply all changes at once
        onMealsChange(currentMeals);

        addToHistory({
            type: 'batch_operations',
            startTime: batchStartTime,
            endTime: Date.now(),
            initialMealCount: initialMeals.length,
            finalMealCount: currentMeals.length
        });

        return { success: true, result };
    }, [meals, onMealsChange, generateUuid, profile, addToHistory, startGlobalCleanup]);

    /**
     * Start adding a meal to a specific offset and mealType
     * Opens the AddSwapRecipe modal for meal selection with nutritional intelligence
     *
     * @param {number} offset - Day offset (0-6)
     * @param {string} mealType - Meal type name
     * @param {Object} options - Additional options
     */
    const startAddMeal = useCallback((offset, mealTypeName, options = {}) => {
        // Analytics tracking for starting to add a recipe
        Analytics.startAddRecipe({
            'Meal Plan UUID': plan.uuid,
            'Meal Plan Name': plan.title,
            'Offset': offset,
            'Meal Type': mealTypeName
        });

        const mealType = getMealTypeInfoByName(mealTypeName, null, plan.meal_types);

        if (!mealType) {
            console.error(`Meal type "${mealTypeName}" not found in profile or plan preferences.`);
            return;
        }

        // Calculate participants for this meal type using the updated function
        const participants = getParticipantsForProfileByMealType(profile, mealType, true);

        // Get all meals for the current day/offset for nutritional context
        const dayMeals = meals.filter(meal =>
            meal.offset === offset &&
            !meal.deleted
        );

        // Get leftover preferences from profile
        const leftoversEnabled = profile.preferences?.leftovers_enabled || false;
        const maxLeftoverDays = profile.preferences?.max_leftover_days || 2;

        // Get modal settings with nutritional intelligence
        const modalSettings = getDefaultAddSwapSettings(
            participants,
            offset,
            mealType,
            [], // No meals being replaced in add mode
            options,
            profile,
            plan,
            recipes,
            foods,
            dayMeals,
            leftoversEnabled,
            maxLeftoverDays
        );

        // Create comprehensive addSwapContext with all required properties
        const addSwapContext = {
            mode: "add",
            offset,
            mealType,
            profile,
            participants,
            plan,
            meals: dayMeals,
            auto_log: false,
            logged_portion: null,
            fullBrowserSearchPlaceholder: "What would you like to add?",
            modalSettings,
            options,
            mealsToDelete: [] // Empty for add mode
        };

        modalManager.openAddSwapRecipeModal(addSwapContext);
    }, [modalManager, plan, profile, meals, recipes]);

    /**
     * Start replacing a meal - extracts destination and finds all meals to delete
     * Opens the AddSwapRecipe modal for meal selection with nutritional intelligence
     *
     * @param {number} offset - Day offset (0-6)
     * @param {string} mealType - Meal type name
     * @param {Array} mealsToDelete - Array of meals being replaced
     * @param {Object} options - Additional options
     */
    const startReplaceMeal = useCallback((offset, mealTypeName, mealsToDelete = [], options= {}) => {
        // Analytics tracking for starting to replace a recipe
        Analytics.startReplaceRecipe({
            'Meal Plan UUID': plan.uuid,
            'Meal Plan Name': plan.title,
            'Offset': offset,
            'Meal Type': mealTypeName
        });

        const mealType = getMealTypeInfoByName(mealTypeName, null, plan.meal_types);

        // Calculate participants for this meal type using the updated function
        const participants = getParticipantsForProfileByMealType(profile, mealTypeName);

        // Expand mealsToDelete to include any leftovers for proper cleanup
        const expandedMealsToDelete = [];
        mealsToDelete.forEach(meal => {
            expandedMealsToDelete.push(meal);
            // Find and include any leftovers for this meal
            const leftovers = getLeftoversForMeal(meal.id || meal.uuid);
            expandedMealsToDelete.push(...leftovers);
        });

        // Index the meals we're about to replace so they're easier to exclude
        const replaceIds = expandedMealsToDelete.map(meal => meal.id);

        // Get all meals for the current day/offset for nutritional context
        const dayMeals = meals.filter(meal =>
            meal.offset === offset &&
            !meal.deleted &&
            !replaceIds.includes(meal.id)
        );

        // Get leftover preferences from profile
        const leftoversEnabled = profile.preferences?.leftovers_enabled || false;
        const maxLeftoverDays = profile.preferences?.max_leftover_days || 2;

        // Get modal settings with nutritional intelligence and recipe exclusions
        const modalSettings = getDefaultAddSwapSettings(
            participants,
            offset,
            mealType,
            mealsToDelete, // Meals being replaced - will be excluded from suggestions
            options,
            profile,
            plan,
            recipes,
            foods,
            dayMeals,
            leftoversEnabled,
            maxLeftoverDays
        );

        // Create comprehensive addSwapContext with all required properties
        const addSwapContext = {
            mode: "replace",
            offset,
            mealType,
            profile,
            participants,
            plan,
            meals: dayMeals,
            auto_log: false,
            logged_portion: null,
            fullBrowserSearchPlaceholder: "What would you like to add?",
            modalSettings,
            options,
            mealsToDelete: expandedMealsToDelete,
            replaceMeals: expandedMealsToDelete // Include for legacy compatibility
        };

        modalManager.openAddSwapRecipeModal(addSwapContext);
    }, [modalManager, plan, profile, meals, recipes]);

    /**
     * Create leftover meal objects from leftover selections
     * This centralizes the leftover creation logic, similar to consumer's updateMealLeftovers
     *
     * @param {Object} baseMeal - The parent meal to create leftovers from
     * @param {Array} leftoverOffsets - Array of leftover selections with {leftoverOffset, mealType, participants, dishPortions}
     * @param {Object|number} scaling - Scaling factor(s) for the leftovers
     * @returns {Array} Array of leftover meal objects ready for persistence
     */
    const createLeftoverMeals = useCallback((baseMeal, leftoverOffsets, scaling) => {
        if (!baseMeal || !leftoverOffsets || leftoverOffsets.length === 0) {
            return [];
        }

        return leftoverOffsets.map((leftover) => {
            // Determine scaling for this leftover
            const leftoverScaling = typeof scaling === 'object'
                ? scaling[baseMeal.id] || scaling[baseMeal.uuid] || baseMeal.scaling || 1
                : scaling || baseMeal.scaling || 1;

            // Create leftover meal object with proper structure
            const leftoverMeal = {
                ...baseMeal,
                id: generateShortUuid(), // Generate proper 8-char UUID
                meal_type: 'leftover',
                parent: baseMeal.id,
                offset: leftover.leftoverOffset,
                meal: leftover.mealType,
                participants: leftover.participants ? leftover.participants.map(p => p.uuid || '').filter(Boolean) : [],
                scaling: leftoverScaling,
                recipe_title: baseMeal.recipe_title ? `${baseMeal.recipe_title} (Leftovers)` : 'Leftovers',
            };

            return leftoverMeal;
        });
    }, []);

    return {
        addMeal,
        addMeals,
        updateMeal,
        removeMeal,
        removeMeals,
        batchOperations,

        // Modal operations
        startAddMeal,
        startReplaceMeal,

        // Rescheduling operations
        rescheduleMeals,
        completeRescheduleMeals,
        applyOverlapResolution,

        // Utilities
        getMealsByType,
        getMealsByOffsetAndType,
        getLeftoversForMeal,
        getMealSlotInfo,
        getRescheduleOverlaps,
        getRescheduleOverlapsV3,
        createLeftoverMeals,

        // Cleanup utilities
        startGlobalCleanup,
        cancelGlobalCleanup,
        restoreAllDeletedMeals,

        // State
        error,
        history: trackHistory ? history : null,

        // Computed values
        mealCount: meals.filter(m => !m.deleted).length,
        hasUnsavedChanges: history.length > 0
    };
};
